import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users, Search, Check, X as XIcon, Trash2, Eye, ArrowLeft,
  Download, Mail, Phone, Globe, Building2, FileText, Send, Loader2,
  Pencil, GitMerge, AlertTriangle
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getLeads, getLeadDetail, updateLead, deleteLead, approveLeadWelcome, getLeadsExportUrl, convertLeadToBuyer, editLead, mergeLeads, findLeadDuplicates } from '../../lib/api';
import { Toaster, toast } from 'sonner';

const ConfirmModal = ({ open, title, message, warning, onConfirm, onCancel, confirmLabel = 'Confirm', danger }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="confirm-modal">
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-white/60 text-sm mb-4">{message}</p>
        {warning && <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs mb-4"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{warning}</span></div>}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-white/60 hover:text-white" data-testid="confirm-cancel-btn">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium ${danger ? 'bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30' : 'bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30'}`} data-testid="confirm-action-btn">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

// --- Lead Detail ---
export const AdminLeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useApp();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingWelcome, setSendingWelcome] = useState(false);
  const [converting, setConverting] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [dupeLoading, setDupeLoading] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getLeadDetail(id);
      setLead(res.data);
      setNotes(res.data.notes || '');
    } catch (err) {
      toast.error(t('adminLeads.failedLoadLead'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const handleStatusChange = async (status) => {
    try {
      await updateLead(id, { status });
      toast.success(`${t('adminLeads.status')}: ${status}`);
      fetchLead();
    } catch (err) {
      toast.error(t('adminLeads.updateFailed'));
    }
  };

  const handleSaveNotes = async () => {
    try {
      await updateLead(id, { notes });
      toast.success(t('adminLeads.notesSaved'));
    } catch (err) {
      toast.error(t('adminLeads.failedSaveNotes'));
    }
  };

  const handleApproveWelcome = async () => {
    setSendingWelcome(true);
    setConfirm(null);
    try {
      const res = await approveLeadWelcome(id);
      if (res.data.email_sent) {
        toast.success(t('adminLeads.leadApprovedWelcome'));
      } else {
        toast.warning(t('adminLeads.leadApprovedNoEmail'));
      }
      fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('adminLeads.failedApprove'));
    } finally {
      setSendingWelcome(false);
    }
  };

  const handleConvertToBuyer = async () => {
    setConverting(true);
    setConfirm(null);
    try {
      const res = await convertLeadToBuyer(id);
      if (res.data.email_sent) {
        toast.success(t('adminLeads.buyerCreatedEmail'));
      } else {
        toast.warning(t('adminLeads.buyerCreatedNoEmail'));
      }
      fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('adminLeads.failedConvert'));
    } finally {
      setConverting(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true);
      await editLead(id, editForm);
      toast.success('Lead updated');
      setEditing(false);
      fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteLead = async () => {
    try {
      setActionLoading(true);
      await deleteLead(id);
      toast.success('Lead deleted');
      navigate('/admin/leads');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  const handleFindDuplicates = async () => {
    try {
      setDupeLoading(true);
      const res = await findLeadDuplicates(id);
      setDuplicates(res.data);
      if (res.data.length === 0) toast.info('No duplicates found');
    } catch (err) {
      toast.error('Failed to find duplicates');
    } finally {
      setDupeLoading(false);
    }
  };

  const handleMerge = async (secondaryId) => {
    try {
      setActionLoading(true);
      await mergeLeads({ primary_id: id, secondary_ids: [secondaryId] });
      toast.success('Records merged successfully');
      setDuplicates(null);
      fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-white/40">{t('common.loading')}</div>;
  if (!lead) return <div className="p-8 text-center text-white/40">{t('adminLeads.leadNotFound')}</div>;

  const statusColor = lead.status === 'approved' ? '#10B981' : lead.status === 'rejected' ? '#EF4444' : lead.status === 'converted' ? '#8B5CF6' : '#F59E0B';

  return (
    <div className="p-6 lg:p-8" data-testid="lead-detail">
      <Toaster position="top-right" theme="dark" />
      <button onClick={() => navigate('/admin/leads')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors" data-testid="back-to-leads">
        <ArrowLeft className="w-4 h-4" /> {t('adminLeads.backToLeads')}
      </button>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="lead-actions">
        {!editing && (
          <button onClick={() => { setEditing(true); setEditForm({ company_name: lead.company_name, contact_name: lead.contact_name, email: lead.email, whatsapp: lead.whatsapp || '', country: lead.country || '', business_type: lead.business_type || '', monthly_volume: lead.monthly_volume || '' }); }} className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white/70 text-sm hover:text-white" data-testid="btn-edit-lead">
            <Pencil className="w-4 h-4" />Edit
          </button>
        )}
        <button onClick={handleFindDuplicates} disabled={dupeLoading} className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-sm hover:bg-purple-500/20" data-testid="btn-find-duplicates">
          <GitMerge className="w-4 h-4" />{dupeLoading ? 'Searching...' : 'Find Duplicates'}
        </button>
        <button onClick={() => setConfirm({ type: 'delete', title: 'Delete Lead', message: `Permanently delete lead "${lead.company_name}"?`, confirmLabel: 'Delete Forever', danger: true })} className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20" data-testid="btn-delete-lead">
          <Trash2 className="w-4 h-4" />Delete
        </button>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="card-dark p-5 mb-6 space-y-3" data-testid="lead-edit-form">
          <h3 className="font-semibold text-sm text-white/60 uppercase">Edit Lead</h3>
          {['company_name', 'contact_name', 'email', 'whatsapp', 'country', 'business_type', 'monthly_volume'].map(f => (
            <div key={f}>
              <label className="text-xs text-white/40 block mb-1">{f.replace(/_/g, ' ')}</label>
              <input value={editForm[f] || ''} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid={`edit-field-${f}`} />
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={handleSaveEdit} disabled={actionLoading} className="px-4 py-2 bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] text-sm font-medium" data-testid="btn-save-edit">Save</button>
            <button onClick={() => setEditing(false)} className="px-4 py-2 text-white/60 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Duplicates panel */}
      {duplicates && duplicates.length > 0 && (
        <div className="card-dark p-5 mb-6 border-purple-500/20" data-testid="duplicates-panel">
          <h3 className="font-semibold text-sm text-purple-400 uppercase mb-3 flex items-center gap-2"><GitMerge className="w-4 h-4" />Potential Duplicates ({duplicates.length})</h3>
          <div className="space-y-2">
            {duplicates.map(d => (
              <div key={d.id} className="flex items-center justify-between p-3 bg-white/5 border border-white/5">
                <div className="text-sm">
                  <span className="text-white font-medium">{d.company_name}</span>
                  <span className="text-white/40 ml-2">{d.email}</span>
                  <span className="text-white/30 ml-2">{d.country}</span>
                </div>
                <button onClick={() => setConfirm({ type: 'merge', title: 'Merge Records', message: `Merge "${d.company_name}" into "${lead.company_name}"? The duplicate will be deleted.`, confirmLabel: 'Merge', mergeId: d.id })} className="px-3 py-1 text-xs bg-purple-500/20 border border-purple-500/30 text-purple-400 hover:bg-purple-500/30" data-testid={`merge-btn-${d.id}`}>
                  Merge into this
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-dark p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold">{lead.company_name}</h1>
              <span className="px-3 py-1 text-xs uppercase tracking-wider font-bold" style={{ backgroundColor: `${statusColor}20`, color: statusColor }}>
                {t(`leadStatus.${lead.status}`) || lead.status}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Building2, label: t('adminLeads.company'), value: lead.company_name },
                { icon: Users, label: t('adminLeads.contact'), value: lead.contact_name },
                { icon: Mail, label: t('adminLeads.email'), value: lead.email },
                { icon: Phone, label: t('adminBuyers.whatsapp'), value: lead.whatsapp || 'N/A' },
                { icon: Globe, label: t('adminLeads.country'), value: lead.country },
                { icon: FileText, label: t('adminLeads.businessType'), value: lead.business_type },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-start gap-3 p-3 bg-[#050505] border border-white/5">
                  <Icon className="w-5 h-5 text-[#00D4FF] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-white/40 uppercase tracking-wider">{label}</p>
                    <p className="text-white font-medium">{value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3 bg-[#050505] border border-white/5">
                <p className="text-xs text-white/40 uppercase tracking-wider mb-1">{t('adminLeads.monthlyVolume')}</p>
                <p className="text-white font-medium">{lead.monthly_volume || t('adminLeads.notSpecified')}</p>
              </div>
              <div className="p-3 bg-[#050505] border border-white/5">
                <p className="text-xs text-white/40 uppercase tracking-wider mb-1">{t('adminLeads.marketingOptIn')}</p>
                <p className={lead.marketing_opt_in ? 'text-[#10B981] font-medium' : 'text-white/40'}>
                  {lead.marketing_opt_in ? t('adminLeads.yes') : t('adminLeads.no')}
                </p>
              </div>
            </div>

            {lead.interested_categories?.length > 0 && (
              <div className="mt-4 p-3 bg-[#050505] border border-white/5">
                <p className="text-xs text-white/40 uppercase tracking-wider mb-2">{t('adminLeads.interestedCategories')}</p>
                <div className="flex flex-wrap gap-2">
                  {lead.interested_categories.map(cat => (
                    <span key={cat} className="px-3 py-1 text-xs bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/20">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 text-xs text-white/30">
              {t('adminLeads.registered')}: {new Date(lead.created_at).toLocaleString(language === 'es' ? 'es' : 'en')}
              {lead.updated_at !== lead.created_at && ` | ${t('adminLeads.updated')}: ${new Date(lead.updated_at).toLocaleString(language === 'es' ? 'es' : 'en')}`}
            </div>
          </div>
        </div>

        {/* Actions sidebar */}
        <div className="space-y-6">
          {/* Quick Approve + Welcome Email */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-2">{t('adminLeads.quickApprove')}</h2>
            <p className="text-xs text-white/40 mb-4">{t('adminLeads.quickApproveDesc')}</p>
            <button
              onClick={() => setConfirm({ type: 'approve', title: 'Approve & Send Welcome', message: `Approve lead "${lead.company_name}" and send welcome email to ${lead.email}?`, confirmLabel: 'Approve & Send' })}
              disabled={sendingWelcome || lead.status === 'approved'}
              className="w-full px-4 py-3 text-sm font-medium bg-[#00D4FF] text-black hover:bg-[#00D4FF]/80 disabled:opacity-30 flex items-center justify-center gap-2 transition-colors"
              data-testid="approve-welcome-btn"
            >
              {sendingWelcome ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t('adminLeads.sending')}</>
              ) : (
                <><Send className="w-4 h-4" /> {t('adminLeads.approveWelcome')}</>
              )}
            </button>
            {lead.welcome_email_sent_at && (
              <div className="mt-3 p-3 bg-[#10B981]/10 border border-[#10B981]/20 text-xs">
                <p className="text-[#10B981] font-medium mb-1">{t('adminLeads.welcomeEmailSent')}</p>
                <p className="text-white/40">{new Date(lead.welcome_email_sent_at).toLocaleString(language === 'es' ? 'es' : 'en')}</p>
              </div>
            )}
          </div>

          {/* Convert to Buyer Account */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-2">{t('adminLeads.convertToBuyerTitle')}</h2>
            <p className="text-xs text-white/40 mb-4">{t('adminLeads.convertToBuyerDesc')}</p>
            <button
              onClick={() => setConfirm({ type: 'convert', title: 'Convert to Buyer', message: `Convert lead "${lead.company_name}" to a buyer account? A password setup email will be sent to ${lead.email}.`, confirmLabel: 'Convert & Send', warning: 'This action creates a buyer account and cannot be easily undone.' })}
              disabled={converting || lead.status === 'converted'}
              className="w-full px-4 py-3 text-sm font-medium bg-[#10B981] text-black hover:bg-[#10B981]/80 disabled:opacity-30 flex items-center justify-center gap-2 transition-colors"
              data-testid="convert-buyer-btn"
            >
              {converting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t('adminLeads.converting')}</>
              ) : lead.status === 'converted' ? (
                <><Check className="w-4 h-4" /> {t('adminLeads.alreadyConverted')}</>
              ) : (
                <><Send className="w-4 h-4" /> {t('adminLeads.convertSendSetup')}</>
              )}
            </button>
            {lead.converted_at && (
              <div className="mt-3 p-3 bg-[#10B981]/10 border border-[#10B981]/20 text-xs">
                <p className="text-[#10B981] font-medium mb-1">{t('adminLeads.convertedToBuyer')}</p>
                <p className="text-white/40">{new Date(lead.converted_at).toLocaleString(language === 'es' ? 'es' : 'en')}</p>
                {lead.converted_by && <p className="text-white/30">{lead.converted_by}</p>}
              </div>
            )}
          </div>

          {/* Status actions */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-4">{t('adminLeads.manualActions')}</h2>
            <div className="space-y-3">
              <button
                onClick={() => handleStatusChange('approved')}
                disabled={lead.status === 'approved'}
                className="w-full px-4 py-3 text-sm font-medium bg-[#10B981] text-black hover:bg-[#10B981]/80 disabled:opacity-30 flex items-center justify-center gap-2 transition-colors"
                data-testid="approve-lead-btn"
              >
                <Check className="w-4 h-4" /> {t('adminLeads.approveNoEmail')}
              </button>
              <button
                onClick={() => handleStatusChange('rejected')}
                disabled={lead.status === 'rejected'}
                className="w-full px-4 py-3 text-sm font-medium bg-[#EF4444] text-white hover:bg-[#EF4444]/80 disabled:opacity-30 flex items-center justify-center gap-2 transition-colors"
                data-testid="reject-lead-btn"
              >
                <XIcon className="w-4 h-4" /> {t('adminLeads.reject')}
              </button>
            </div>
            {lead.approved_at && (
              <div className="mt-3 text-xs text-white/30">
                {t('adminLeads.approved')}: {new Date(lead.approved_at).toLocaleString(language === 'es' ? 'es' : 'en')}
                {lead.approved_by && ` — ${lead.approved_by}`}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-4">{t('adminLeads.adminNotes')}</h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder={t('adminLeads.notesPlaceholder')}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] mb-3 text-sm"
              data-testid="lead-notes"
            />
            <button onClick={handleSaveNotes} className="btn-secondary w-full !py-2 text-xs" data-testid="save-notes-btn">
              {t('adminLeads.saveNotes')}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        warning={confirm?.warning}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.type === 'delete') handleDeleteLead();
          else if (confirm?.type === 'merge') handleMerge(confirm.mergeId);
          else if (confirm?.type === 'approve') handleApproveWelcome();
          else if (confirm?.type === 'convert') handleConvertToBuyer();
        }}
      />
    </div>
  );
};

// --- Leads List ---
export const AdminLeads = () => {
  const navigate = useNavigate();
  const { t, language } = useApp();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [optInFilter, setOptInFilter] = useState('');
  const [confirm, setConfirm] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (countryFilter) params.country = countryFilter;
      if (optInFilter === 'true') params.opt_in = true;
      if (search) params.search = search;
      const res = await getLeads(params);
      setLeads(res.data);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, countryFilter, optInFilter, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleDelete = async (id) => {
    try {
      await deleteLead(id);
      toast.success(t('adminLeads.leadDeleted'));
      setConfirm(null);
      fetchLeads();
    } catch (err) {
      toast.error(t('adminLeads.failedDeleteLead'));
    }
  };

  const handleExportCSV = () => {
    const token = localStorage.getItem('luxortek_admin_token');
    const url = getLeadsExportUrl();
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Export failed');
        const disposition = res.headers.get('content-disposition') || '';
        const filename = disposition.split('filename=')[1]?.replace(/"/g, '') || 'leads_export.csv';
        return res.blob().then(blob => ({ blob, filename }));
      })
      .then(({ blob, filename }) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(t('adminLeads.csvExported'));
      })
      .catch(() => toast.error(t('adminLeads.exportFailed')));
  };

  const countries = [...new Set(leads.map(l => l.country).filter(Boolean))].sort();

  const getStatusColor = (status) => {
    if (status === 'approved') return '#10B981';
    if (status === 'rejected') return '#EF4444';
    if (status === 'converted') return '#8B5CF6';
    return '#F59E0B';
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-leads">
      <Toaster position="top-right" theme="dark" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Users className="w-8 h-8 text-[#00D4FF]" />
            {t('adminLeads.title')}
          </h1>
          <p className="text-white/60 mt-1">{leads.length} {t('adminLeads.leads')}</p>
        </div>
        <button
          onClick={handleExportCSV}
          className="btn-secondary flex items-center gap-2"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4" />
          {t('adminLeads.exportOptInCsv')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            placeholder={t('adminLeads.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF]"
            data-testid="lead-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] min-w-[140px]"
          data-testid="lead-status-filter"
        >
          <option value="">{t('adminLeads.allStatuses')}</option>
          <option value="pending">{t('adminBuyers.pending')}</option>
          <option value="approved">{t('adminBuyers.approved')}</option>
          <option value="rejected">{t('adminLeads.reject')}</option>
        </select>
        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] min-w-[140px]"
          data-testid="lead-country-filter"
        >
          <option value="">{t('adminLeads.allCountries')}</option>
          {countries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={optInFilter}
          onChange={(e) => setOptInFilter(e.target.value)}
          className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] min-w-[120px]"
          data-testid="lead-optin-filter"
        >
          <option value="">{t('adminLeads.allOptIn')}</option>
          <option value="true">{t('adminLeads.optedIn')}</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="card-dark p-8 text-center text-white/40">{t('common.loading')}</div>
      ) : leads.length === 0 ? (
        <div className="card-dark p-8 text-center text-white/40">{t('adminLeads.noLeads')}</div>
      ) : (
        <div className="card-dark overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-terminal">
              <thead>
                <tr>
                  <th className="text-left">{t('adminLeads.date')}</th>
                  <th className="text-left">{t('adminLeads.company')}</th>
                  <th className="text-left">{t('adminLeads.country')}</th>
                  <th className="text-left">{t('adminLeads.email')}</th>
                  <th className="text-left">{t('adminBuyers.whatsapp')}</th>
                  <th className="text-left">{t('adminLeads.type')}</th>
                  <th className="text-center">{t('adminLeads.optIn')}</th>
                  <th className="text-center">{t('adminLeads.status')}</th>
                  <th className="text-right">{t('adminLeads.action')}</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                    <td className="text-white/50 text-xs whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleDateString(language === 'es' ? 'es' : 'en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="font-medium">{lead.company_name}</td>
                    <td className="text-white/60">{lead.country}</td>
                    <td className="text-[#00D4FF] text-sm">{lead.email}</td>
                    <td className="text-white/50 text-sm">{lead.whatsapp || '\u2014'}</td>
                    <td className="text-white/60 text-sm">{lead.business_type}</td>
                    <td className="text-center">
                      {lead.marketing_opt_in ? (
                        <Check className="w-4 h-4 text-[#10B981] mx-auto" />
                      ) : (
                        <XIcon className="w-4 h-4 text-white/20 mx-auto" />
                      )}
                    </td>
                    <td className="text-center">
                      <span
                        className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold"
                        style={{ backgroundColor: `${getStatusColor(lead.status)}20`, color: getStatusColor(lead.status) }}
                      >
                        {t(`leadStatus.${lead.status}`) || lead.status}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/admin/leads/${lead.id}`)}
                          className="p-2 text-white/40 hover:text-[#00D4FF] transition-colors"
                          title={t('common.view')}
                          data-testid={`view-lead-${lead.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfirm({ type: 'delete', id: lead.id, title: 'Delete Lead', message: `Permanently delete lead "${lead.company_name}"?`, confirmLabel: 'Delete Forever', danger: true })}
                          className="p-2 text-white/20 hover:text-[#EF4444] transition-colors"
                          title={t('common.delete')}
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmation Modal for list-level delete */}
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.type === 'delete') handleDelete(confirm.id);
        }}
      />
    </div>
  );
};
